import { deletePhoneNumber } from '@api/apiCall';
import { Button, Text, View } from '@components/primitives';
import { useToast } from '@hooks';
import BottomSheet from '@molecules/LoginInfoSheet';
import PinWithAddPhone from '@molecules/PinWithAddPhone';
import { shouldShowError } from '@organisms/HomeTab/homeUtils';
import { useNavigation } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import {
  BOTTONSHEET_PHONE_CONTENT,
  BOTTONSHEET_PHONE_ENROLLMENT,
  COMMON_CONSTANTS,
  addPhoneTemplate2,
} from '@root/constants';
import { getStore } from '@store/storeUtils';
import { useMutation } from '@tanstack/react-query';
import colors from '@theme/colors';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS, AMPLITUDE_EVENT_SOURCES } from '@utils/analytics/constants';
import { verticalScale } from '@utils/normalize';
import {
  formatPhoneNumber,
  formatUnderScoreLowerCase,
  returnBottomSheetContent,
  showStatusViewForPhone,
} from '@utils/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, Linking, TouchableOpacity } from 'react-native';
import { getBottomSheetHandlers, onDeletePhoneCompleteCallBack, shouldShowFooterComponent } from './phoneListUtils';
import styles from './styles';

// Define interfaces
interface PhoneItem {
  mobileNumber: string;
  ownerName: string;
  status: string;
}

interface InfoModal {
  visible: boolean;
  data: {
    hasTwoButtons: boolean;
  };
  errorCode: string;
  currentId: number;
  selectedNumber: string;
  selectedName: string;
}

interface PhoneListProps {
  transactionCategoryId?: string;
  phoneList: PhoneItem[];
  getPhoneNumbers: () => void;
  isTransact: boolean;
}

interface Store {
  setBPPhone: (phone: { mobileNumber: string; name: string }) => void;
  setInitialData: () => void;
  bpPhone: { mobileNumber: string; name: string };
  setLoading: (loading: boolean) => void;
}

export function onPressHandler(
  item: PhoneItem,
  setBPPhone: (phone: { mobileNumber: string; name: string }) => void,
  navigation: any,
  isTransact: boolean,
  transactionCategoryId: string,
  eventSource: string,
) {
  return () => {
    if (isTransact) {
      const e = AMPLITUDE_CONSTANTS.PhoneEnroll.phonenumber_selected;
      AmplitudeHelper.logEvent(e.name, {
        [e.attributes.source]: eventSource,
        [e.attributes.transaction_category_id]: formatUnderScoreLowerCase(transactionCategoryId),
        [e.attributes.selected_phone_number]: formatPhoneNumber(item.mobileNumber),
      });
    }
    if (isTransact && item.status.toUpperCase() === 'APPROVED') {
      setBPPhone({ mobileNumber: item.mobileNumber, name: item.ownerName });
      navigation.pop();
    }
  };
}

const onContactSupport = (eventSource: string): void => {
  AmplitudeHelper.logEvent(AMPLITUDE_CONSTANTS.Login.helpClicked.name, {
    [AMPLITUDE_CONSTANTS.Login.helpClicked.attributes.source]: eventSource,
  });
  Linking.openURL(COMMON_CONSTANTS.support);
};

const PhoneList: React.FC<PhoneListProps> = ({
  phoneList,
  getPhoneNumbers,
  isTransact,
  transactionCategoryId = '',
}) => {
  const { t } = useTranslation('common');
  const { setBPPhone, setInitialData, bpPhone } = getStore() as Store;
  const navigation = useNavigation();
  const initalState: InfoModal = {
    visible: false,
    data: returnBottomSheetContent(
      BOTTONSHEET_PHONE_ENROLLMENT.PHONE_DELETE,
      BOTTONSHEET_PHONE_CONTENT[BOTTONSHEET_PHONE_ENROLLMENT.PHONE_DELETE],
    ),
    errorCode: BOTTONSHEET_PHONE_ENROLLMENT.PHONE_DELETE,
    currentId: 0,
    selectedNumber: '',
    selectedName: '',
  };
  const { setLoading } = getStore() as Store;
  const toast = useToast();

  const [infoModal, setInfoModal] = useState<InfoModal>(initalState);

  const {
    isLoading,
    isError,
    isSuccess,
    error,
    data,
    mutate: deletePhoneNumberMutate,
  } = useMutation({
    mutationFn: (payload: unknown) => deletePhoneNumber(payload),
  });

  const eventSource = isTransact ? AMPLITUDE_EVENT_SOURCES.select_phone_page : AMPLITUDE_EVENT_SOURCES.phone_list_page;

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  useEffect(() => {
    if (isSuccess && data) {
      onDeletePhoneCompleteCallBack(infoModal, getPhoneNumbers, setInfoModal, initalState);
    }
  }, [isSuccess, data, infoModal, getPhoneNumbers]);

  useEffect(() => {
    shouldShowError(isError, error, toast, t, navigation, setInitialData);
  }, [isError, error]);

  const { onPressPrimaryBottomSHeet, onPressSecondaryBottomSheet, onBottomSheetCloseHandler } = getBottomSheetHandlers(
    infoModal,
    setInfoModal,
    deletePhoneNumberMutate,
    initalState,
  );

  const footerComponent = (): React.JSX.Element | null => {
    if (phoneList && phoneList.length > 0) {
      const pendingList = phoneList.filter(item => item.status.toUpperCase() === 'PENDING');
      const rejectedList = phoneList.filter(item => item.status.toUpperCase() === 'REJECTED');
      const hasPending = pendingList.length > 0;
      const hasAllRejected = rejectedList.length === phoneList.length;
      if (pendingList.length === 10) {
        return null;
      }
      return (
        <View
          style={[
            appStyles.borderWithShadow,
            appStyles.marginHorizontal(0),
            appStyles.marginTop(0),
            appStyles.paddingHorizontal(0),
            appStyles.paddingTop(0),
            appStyles.borderTopLeftRadius(0),
            appStyles.borderTopRightRadius(0),
            appStyles.borderBottomLeftRadius(0),
            appStyles.borderBottomRightRadius(0),
          ]}>
          <View style={styles.footerParent}>
            {hasPending || hasAllRejected ? (
              <Button
                testID="btnWhatsappLink"
                variant="ghost"
                onPress={() => onContactSupport(eventSource)}
                paddingY={verticalScale(8)}
                style={styles.btnWhatsappStyles}
                _text={{ variant: 'md-bold', color: colors.variants.clover, minW: '90px' }}>
                {t('phoneTransfer.phoneTransferSupport')}
              </Button>
            ) : null}
            <PinWithAddPhone
              type={addPhoneTemplate2}
              listData={phoneList}
              pageSource={eventSource}
              isTransact={isTransact}
              transactionCategoryId={transactionCategoryId}
            />
          </View>
        </View>
      );
    }
    return null;
  };

  return (
    <View style={appStyles.flex1}>
      <View style={[appStyles.flex1, appStyles.marginHorizontal(16)]}>
        <FlatList
          testID="fltLstPhoneNumbers"
          style={styles.fltLstStyles}
          data={phoneList}
          showsHorizontalScrollIndicator={false}
          renderItem={({ item, index }) => (
            <TouchableOpacity
              testID={`phoneItem${index}`}
              style={styles.phoneListContainer}
              onPress={onPressHandler(item, setBPPhone, navigation, isTransact, transactionCategoryId, eventSource)}>
              <View style={styles.phoneOwnerContainer}>
                <Text variant="sm-normal">{item.mobileNumber}</Text>
                <Text variant="sm-bold" color={colors.neutral.darkMist}>
                  {item.ownerName}
                </Text>
              </View>
              {showStatusViewForPhone(item, t, isTransact, bpPhone)}
            </TouchableOpacity>
          )}
        />
      </View>
      {shouldShowFooterComponent(isTransact, footerComponent)}
      <BottomSheet
        onPressPrimary={onPressPrimaryBottomSHeet}
        onPressSecondary={onPressSecondaryBottomSheet}
        loginInfo={infoModal}
        hasTwoButtons={infoModal.data.hasTwoButtons}
        onClose={onBottomSheetCloseHandler}
      />
    </View>
  );
};

export default PhoneList;
